import React from 'react';
import { toast } from 'react-toastify';
import { BedDouble, X } from 'lucide-react';
import Badge, { readinessVariant } from '../ui/Badge';
import AnnouncementHospitalService from '../../services/AnnouncementHospitalService';

const STATUS_OPTIONS = ['Notified', 'Preparing', 'Ready'];
const STATUS_VARIANT = { Notified: 'amber', Preparing: 'blue', Ready: 'green' };

// Hospitals directly prepared for this incident (see AnnouncementHospital) — separate
// from a team's home hospital, which only gets involved indirectly via team assignment.
function HospitalRoster({ hospitals = [], onUpdated }) {
    const cycleStatus = async (h) => {
        const nextIndex = (STATUS_OPTIONS.indexOf(h.status) + 1) % STATUS_OPTIONS.length;
        const status = STATUS_OPTIONS[nextIndex];
        try {
            await AnnouncementHospitalService.update(h.id, { status });
            onUpdated?.();
        } catch (err) {
            toast.error('Failed to update status.');
        }
    };

    const unassign = async (h) => {
        try {
            await AnnouncementHospitalService.remove(h.id);
            toast.success(`${h.hospital?.name || 'Hospital'} removed from this incident.`);
            onUpdated?.();
        } catch (err) {
            toast.error('Failed to remove hospital.');
        }
    };

    if (hospitals.length === 0) {
        return (
            <div className="strategy-card text-center text-sm text-neutral-400 py-10 dark:text-neutral-500">
                No hospitals prepared for this incident yet.
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {hospitals.map((h) => (
                <div key={h.id} className="strategy-card">
                    <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                            <h4 className="font-bold text-neutral-900 text-sm dark:text-neutral-50">{h.hospital?.name || 'Unknown'}</h4>
                            {h.hospital?.code && <p className="text-xs text-neutral-500 dark:text-neutral-500">{h.hospital.code}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={() => unassign(h)}
                            className="text-neutral-400 hover:text-red-600 dark:text-neutral-500 dark:hover:text-red-400"
                            title="Remove from incident"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex items-center gap-2 mb-3 text-xs text-neutral-600 dark:text-neutral-300">
                        <BedDouble className="w-3.5 h-3.5" />
                        {h.hospital?.bedCapacity != null ? `${h.hospital.bedCapacity} beds` : 'Bed capacity unknown'}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {h.hospital?.readinessLevel && (
                            <Badge variant={readinessVariant(h.hospital.readinessLevel)}>{h.hospital.readinessLevel}</Badge>
                        )}
                        <button
                            type="button"
                            onClick={() => cycleStatus(h)}
                            className="inline-flex"
                            title="Click to advance status"
                        >
                            <Badge variant={STATUS_VARIANT[h.status] || 'neutral'}>{h.status}</Badge>
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default HospitalRoster;
